import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { customersApi } from "@/features/customers/services/customersApi";
import { useAllCropTypes } from "@/features/production/hooks/useCropTypes";
import { ReportTable } from "../components/ReportTable";
import { useSowingBreakdownReport } from "../hooks/useSowingBreakdownReport";
import {
  sowingBreakdownReportApi,
  type SowingBreakdownFilters,
  type SowingBreakdownSource,
} from "../services/sowingBreakdownReportApi";

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const defaultLast7Days = (): SowingBreakdownFilters => {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 6); // inclusive 7-day window
  return {
    startDate: toIsoDate(sevenDaysAgo),
    endDate: toIsoDate(today),
  };
};

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const formatQty = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-KE").format(value);
};

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value);
};

const formatOptionalDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const triggerFileDownload = (blob: Blob, fileName: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const SowingBreakdownReportPage = () => {
  const { hasPermission } = useAuthStore();
  const canViewFinancials = hasPermission("orders.view_financials");

  const initialFilters = useMemo(() => defaultLast7Days(), []);
  const [filters, setFilters] = useState<SowingBreakdownFilters>(initialFilters);
  const [submittedFilters, setSubmittedFilters] =
    useState<SowingBreakdownFilters>(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Auto-run the report on first mount so the user sees the last-7-days view
  // immediately without having to click "Run Report".
  const [runToken, setRunToken] = useState(1);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const [customerSearch, setCustomerSearch] = useState("");
  const debouncedCustomerSearch = useDebounce(customerSearch, 250);
  const customerSearchQuery = useQuery({
    queryKey: ["customers", "search", debouncedCustomerSearch],
    queryFn: () =>
      customersApi.getCustomers({
        pageNumber: 1,
        pageSize: 20,
        search: debouncedCustomerSearch || undefined,
      }),
  });
  const customers = customerSearchQuery.data?.items ?? [];

  const { data: cropTypes = [] } = useAllCropTypes();
  const [cropTypeSearch, setCropTypeSearch] = useState("");
  const filteredCropTypes = useMemo(() => {
    const term = cropTypeSearch.trim().toLowerCase();
    if (!term) return cropTypes;
    return cropTypes.filter((ct) => ct.name.toLowerCase().includes(term));
  }, [cropTypes, cropTypeSearch]);

  const query = useSowingBreakdownReport(submittedFilters, page, pageSize, runToken);
  const data = query.data;

  // Reset page to 1 whenever the user changes pagination size after a query has run
  useEffect(() => {
    if (runToken > 0) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const updateFilter = (key: keyof SowingBreakdownFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const updateSourceFilter = (value: SowingBreakdownSource | "") => {
    setFilters((prev) => ({ ...prev, source: value === "" ? undefined : value }));
  };

  const handleRunReport = () => {
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setValidationError("Start date cannot be after end date.");
      return;
    }
    setValidationError(null);
    setSubmittedFilters(filters);
    setPage(1);
    setRunToken((t) => t + 1);
  };

  const handleResetFilters = () => {
    setFilters(defaultLast7Days());
    setCustomerSearch("");
    setCropTypeSearch("");
    setValidationError(null);
  };

  const canExport = runToken > 0 && !!data && !validationError;

  const handleExportExcel = async () => {
    if (!canExport) return;
    setIsExportingExcel(true);
    try {
      const { blob, fileName } =
        await sowingBreakdownReportApi.exportSowingBreakdownReportExcel(submittedFilters);
      triggerFileDownload(blob, fileName);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    if (!canExport) return;
    setIsExportingPdf(true);
    try {
      const { blob, fileName } =
        await sowingBreakdownReportApi.exportSowingBreakdownReportPdf(submittedFilters);
      triggerFileDownload(blob, fileName);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const formatQuantityWithUnit = (
    qty: number | null | undefined,
    unit: string | null | undefined
  ) => {
    if (qty === null || qty === undefined) return "-";
    const formatted = formatQty(qty);
    return unit ? `${formatted} ${unit}` : formatted;
  };

  // The backend tags no-order rows as "Standalone"; the team uses "Company seeds"
  // in conversation, so swap the display label here without changing API values.
  const STANDALONE_DISPLAY = "Company seeds";
  const displayCustomerName = (name: string) =>
    name === "Standalone" ? STANDALONE_DISPLAY : name;
  const displaySource = (source: string) =>
    source === "Standalone" ? STANDALONE_DISPLAY : source;

  const columns = useMemo(() => {
    const cols = [
      "Customer Name",
      "Source",
      "Crop Type",
      "Crop Variety",
      "Tray Type",
      "Date Sowed",
      "Seedling Date",
      "Quantity",
    ];
    if (canViewFinancials) {
      cols.push("Amount", "Amount Paid", "Balance Payable");
    }
    cols.push("Order #");
    return cols;
  }, [canViewFinancials]);

  const rows = useMemo(() => {
    return (data?.items ?? []).map((row) => {
      const cells: string[] = [
        displayCustomerName(row.customerName),
        displaySource(row.source),
        row.cropTypeName,
        row.variety ?? "-",
        row.trayType ?? "-",
        formatDate(row.dateSowed),
        formatOptionalDate(row.seedlingDate),
        formatQuantityWithUnit(row.quantity, row.quantityUnit),
      ];
      if (canViewFinancials) {
        cells.push(
          formatCurrency(row.amount),
          formatCurrency(row.amountPaid),
          formatCurrency(row.balancePayable)
        );
      }
      cells.push(row.orderNumber ?? "-");
      return cells;
    });
  }, [data, canViewFinancials]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Sowing Breakdown</h1>
        <p className="text-gray-500 mt-1">
          Historical sown items — one row per actually-sown instruction. Defaults to the last
          7 days; filter by date range, customer, crop type, or source.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Field className="lg:col-span-2">
              <FieldLabel>Date Range</FieldLabel>
              <DateRangePicker
                startDate={filters.startDate ?? ""}
                endDate={filters.endDate ?? ""}
                onChange={({ startDate, endDate }) => {
                  updateFilter("startDate", startDate ?? "");
                  updateFilter("endDate", endDate ?? "");
                }}
              />
            </Field>

            <Field>
              <FieldLabel>Customer</FieldLabel>
              <Combobox
                value={filters.customerId ?? null}
                onValueChange={(value: string | null) => {
                  updateFilter("customerId", value ?? "");
                  if (value) {
                    const chosen = customers.find((c) => c.id === value);
                    if (chosen) setCustomerSearch(chosen.customerName);
                  } else {
                    setCustomerSearch("");
                  }
                }}
                inputValue={customerSearch}
              >
                <ComboboxInput
                  placeholder="Search customers… (leave empty for all)"
                  showClear={!!filters.customerId}
                  className="w-full"
                  onChange={(e) => setCustomerSearch(e.target.value)}
                />
                <ComboboxContent>
                  <ComboboxList>
                    {customerSearchQuery.isFetching && customers.length === 0 && (
                      <ComboboxEmpty>Searching…</ComboboxEmpty>
                    )}
                    {!customerSearchQuery.isFetching && customers.length === 0 && (
                      <ComboboxEmpty>No customers found.</ComboboxEmpty>
                    )}
                    {customers.map((c) => (
                      <ComboboxItem key={c.id} value={c.id}>
                        {c.customerName}
                      </ComboboxItem>
                    ))}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </Field>

            <Field>
              <FieldLabel>Crop Type</FieldLabel>
              <Combobox
                value={filters.cropTypeId ?? null}
                onValueChange={(value: string | null) => {
                  updateFilter("cropTypeId", value ?? "");
                  if (value) {
                    const chosen = cropTypes.find((ct) => ct.id === value);
                    if (chosen) setCropTypeSearch(chosen.name);
                  } else {
                    setCropTypeSearch("");
                  }
                }}
                inputValue={cropTypeSearch}
              >
                <ComboboxInput
                  placeholder="Search crop types… (leave empty for all)"
                  showClear={!!filters.cropTypeId}
                  className="w-full"
                  onChange={(e) => setCropTypeSearch(e.target.value)}
                />
                <ComboboxContent>
                  <ComboboxList>
                    {filteredCropTypes.length === 0 && (
                      <ComboboxEmpty>No crop types found.</ComboboxEmpty>
                    )}
                    {filteredCropTypes.map((ct) => (
                      <ComboboxItem key={ct.id} value={ct.id}>
                        {ct.name}
                      </ComboboxItem>
                    ))}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </Field>

            <Field>
              <FieldLabel>Source</FieldLabel>
              <Select
                value={filters.source ?? "__all"}
                onValueChange={(value) =>
                  updateSourceFilter(value === "__all" ? "" : (value as SowingBreakdownSource))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="standalone">Company seeds</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {validationError && (
            <p className="text-sm text-red-600">{validationError}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleRunReport} disabled={query.isFetching}>
              {query.isFetching ? "Running…" : "Run Report"}
            </Button>
            <Button variant="outline" onClick={handleResetFilters}>
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={!canExport || isExportingExcel}
            >
              {isExportingExcel ? "Exporting…" : "Export Excel"}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={!canExport || isExportingPdf}
            >
              {isExportingPdf ? "Exporting…" : "Export PDF"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {query.isError && (
        <p className="text-sm text-red-600">
          Failed to load the sowing breakdown report. Try again.
        </p>
      )}

      {runToken > 0 && (
        <div className="space-y-3">
          <ReportTable
            title="Sowing Breakdown"
            columns={columns}
            rows={rows}
            emptyMessage={
              query.isFetching ? "Loading…" : "No sown instructions match the filters."
            }
          />

          {data && data.totalCount > 0 && (
            <PaginationBar
              currentPage={data.currentPage}
              pageSize={data.pageSize}
              totalCount={data.totalCount}
              totalPages={data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      )}
    </div>
  );
};
